import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import SiteShell from "../site/SiteShell";
import PageHero from "../site/PageHero";
import { Reveal } from "../site/motion";
import { CLIENT_SERVICES, CLIENT_TYPES, CLIENT_BENEFITS } from "../site/content";

export default function ForClients() {
  return (
    <SiteShell>
      <PageHero
        overline="For Clients"
        title="We don't just find contractors. We help build better supply chains."
        kicker="Verified Workforce provides principal contractors, developers, housing associations and councils with access to a developing network of specialist construction businesses — reviewed for compliance, commercial readiness and capability."
        image="https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1800&q=80"
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/contact" className="vw-btn vw-btn--primary" data-testid="clients-hero-cta">
            Discuss Your Supply Chain Requirements
            <ArrowUpRight size={16} />
          </Link>
          <Link to="/opportunities" className="vw-btn vw-btn--ghost">
            View Current Opportunities
          </Link>
        </div>
      </PageHero>

      {/* Client types */}
      <section className="vw-section bg-[var(--vw-paper)]" data-testid="clients-types-section">
        <div className="vw-container">
          <Reveal>
            <div className="vw-overline mb-4">Who We Support</div>
            <h2 className="font-display text-3xl lg:text-5xl font-semibold text-[var(--vw-navy)] tracking-tight max-w-3xl">
              Partnering with the organisations delivering UK construction.
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CLIENT_TYPES.map((t, i) => (
              <Reveal key={t} delay={i * 0.04}>
                <div className="vw-card p-6 h-full">
                  <div className="font-mono text-xs text-[var(--vw-gold)] tracking-[0.22em]">
                    0{i + 1}
                  </div>
                  <div className="mt-4 font-display text-lg font-semibold text-[var(--vw-navy)]">
                    {t}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="vw-section bg-white" data-testid="clients-benefits-section">
        <div className="vw-container">
          <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="vw-overline mb-4">Why Work With Us</div>
                <h2 className="font-display text-3xl lg:text-5xl font-semibold text-[var(--vw-navy)] tracking-tight leading-[1.05]">
                  Four reasons major clients choose Verified Workforce.
                </h2>
                <p className="mt-6 text-[var(--vw-muted)] leading-relaxed">
                  We develop capability within the supply chain itself — not just introduce
                  businesses. That means stronger partners, better compliance, and more
                  resilient delivery.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
              {CLIENT_BENEFITS.map((b, i) => (
                <Reveal key={b.title} delay={i * 0.07}>
                  <div className="vw-card p-8 h-full">
                    <div className="font-mono text-[11px] tracking-[0.22em] text-[var(--vw-blue)] uppercase">
                      Benefit 0{i + 1}
                    </div>
                    <div className="mt-4 font-display text-xl font-semibold text-[var(--vw-navy)]">
                      {b.title}
                    </div>
                    <p className="mt-3 text-sm text-[var(--vw-muted)] leading-relaxed">{b.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="vw-section bg-[var(--vw-navy)] text-white" data-testid="clients-services-section">
        <div className="vw-container">
          <Reveal>
            <div className="vw-overline vw-overline--gold mb-4">Supply-Chain Services</div>
            <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05] max-w-3xl">
              Everything you need to extend, develop and strengthen your supply chain.
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {CLIENT_SERVICES.map((s, i) => (
              <Reveal key={s} delay={i * 0.04}>
                <div className="vw-card vw-card--dark h-full">
                  <div className="font-mono text-[11px] tracking-[0.22em] text-[var(--vw-gold)]">
                    0{i + 1}
                  </div>
                  <div className="mt-4 font-display text-lg font-medium text-white flex items-start gap-2">
                    <CheckCircle2 size={18} className="text-[var(--vw-gold)] mt-1 flex-shrink-0" />
                    {s}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-12 flex flex-wrap gap-3">
              <Link to="/contact" className="vw-btn vw-btn--gold" data-testid="clients-services-cta">
                Discuss Your Supply Chain Requirements
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Social Value */}
      <section className="vw-section bg-[var(--vw-cream)]" data-testid="clients-social-value">
        <div className="vw-container grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="vw-overline mb-4">SME &amp; Supply-Chain Development</div>
              <h2 className="font-display text-3xl lg:text-5xl font-semibold text-[var(--vw-navy)] tracking-tight leading-[1.05]">
                Growing local supply chains.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.08}>
              <p className="text-base lg:text-lg text-[var(--vw-charcoal)] leading-relaxed">
                Verified Workforce aims to create a bridge between major construction
                opportunities and capable local SMEs. By helping smaller contractors
                strengthen compliance, commercial readiness and access to appropriate
                support, we aim to create a broader pool of businesses capable of
                competing for and delivering larger contracts.
              </p>
              <ul className="mt-6 grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm text-[var(--vw-charcoal)]">
                {["SME participation", "Local supply-chain development", "Supply-chain resilience", "Increased competition", "Contractor development", "Local economic opportunity"].map((s) => (
                  <li key={s} className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-[var(--vw-blue)] mt-0.5 flex-shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
